<template>
    <form id="template-form" :class="{ loading }">
        <Loader :loading="loading"></Loader>
        <label required for="name">{{ $t("template.form.name") }}</label> <input id="name" autofocus v-model="name" class="form-control" />
        <label required for="ref">{{ $t("template.form.ref") }}</label> <input id="ref" v-model="ref" class="form-control" />

        <label required>{{ $t("template.form.zone.label") }}</label>
        <Multiselect
            class="uppercase"
            v-model="selectedZones"
            :options="zones"
            :multiple="true"
            :show-labels="true"
            :preserve-search="true"
            :placeholder="$t('template.form.zone.empty')"
            label="name"
            track-by="name"
            exact
        ></Multiselect>

        <label required>{{ $t("template.form.displayerType.label") }}</label>
        <multiselect
            v-model="displayerType"
            :options="displayerTypes"
            label="name"
            track-by="name"
            :searchable="true"
            :close-on-select="true"
            :show-labels="true"
            :placeholder="$t('template.form.displayerType.empty')"
        ></multiselect>

        <label required>{{ $t("template.form.standDesign.label") }}</label>
        <multiselect
            v-model="standDesign"
            :options="standDesigns"
            label="name"
            track-by="name"
            :searchable="true"
            :close-on-select="true"
            :show-labels="true"
            :placeholder="$t('template.form.standDesign.empty')"
        ></multiselect>
    </form>
</template>

<script>
import Multiselect from "vue-multiselect";
import Loader from "@/components/loader/loader";
import zoneService from "@/services/zone";
import standDesignService from "@/services/stand-design";
import displayerTypeService from "@/services/displayerType";
import { injectVariables } from "@/utils/string";

export default {
    components: { Loader, Multiselect },
    props: {
        value: { type: Object, required: true }
    },
    data: () => ({
        data: {
            name: "",
            ref: "",
            zoneIds: [],
            displayerType: undefined,
            standDesignId: undefined
        },
        loading: true,
        zones: [],
        displayerTypes: [],
        standDesigns: []
    }),
    mounted() {
        this.fetchAll();
        this.data = { ...this.value };
    },
    watch: {
        value() {
            this.data = { ...this.value };
        }
    },
    methods: {
        async fetchStandDesigns() {
            this.standDesigns = await standDesignService.getAll();
        },
        async fetchZones() {
            this.zones = await zoneService.getAll();
        },
        async fetchDisplayerTypes() {
            this.displayerTypes = (await displayerTypeService.get()).data;
        },
        async fetchAll() {
            this.loading = true;
            try {
                await Promise.all([this.fetchZones(), this.fetchStandDesigns(), this.fetchDisplayerTypes()]);
            } catch (err) {
                this.dialogError(err);
            } finally {
                this.loading = false;
            }
        },
        // method called using refs by ancestors
        // throws if a required field is not filled, otherwise returns the form content
        submit() {
            if (this.loading) {
                throw Error(this.$t("template.form.loadingError"));
            }

            const throwMissing = (name, value) => {
                if (!value || (typeof value === "string" && !value.trim().length)) {
                    throw Error(injectVariables(this.$t("template.form.missingError"), { name }));
                }
            };

            const { name, assemblyNumber, zones, displayerType, standDesign } = this.data;
            throwMissing(this.$t("template.form.name"), name);
            throwMissing(this.$t("template.form.ref"), assemblyNumber);
            throwMissing(this.$t("template.form.zone.label"), zones.length);
            throwMissing(this.$t("template.form.displayerType.label"), displayerType && displayerType.id);
            throwMissing(this.$t("template.form.standDesign.label"), standDesign && standDesign.id);

            return {
                name: name.trim(),
                ref: assemblyNumber.trim(),
                zoneIds: zones.map(z => z.id),
                displayerTypeId: displayerType.id,
                standDesignId: standDesign.id
            };
        }
    },
    computed: {
        // Computeds with setters, because we don't want to mutate our props unless the value are valids
        ref: {
            get() {
                return this.data.assemblyNumber;
            },
            set(assemblyNumber) {
                this.data = { ...this.data, assemblyNumber };
                this.$emit("input", this.data);
            }
        },
        name: {
            get() {
                return this.data.name;
            },
            set(name) {
                this.data = { ...this.data, name };
                this.$emit("input", this.data);
            }
        },
        selectedZones: {
            get() {
                return this.zones.filter(zone => this.data.zones.some(z => z.id === zone.id));
            },
            set(zones) {
                this.data = { ...this.data, zones };
                this.$emit("input", this.data);
            }
        },
        displayerType: {
            get() {
                return this.displayerTypes.find(displayer => displayer.id === (this.data.displayerType && this.data.displayerType.id));
            },
            set(displayerType) {
                this.data = { ...this.data, displayerType };
                this.$emit("input", this.data);
            }
        },
        standDesign: {
            get() {
                return this.standDesigns.find(standDesign => standDesign.id === (this.data.standDesign && this.data.standDesign.id));
            },
            set(standDesign) {
                this.data = { ...this.data, standDesign };
                this.$emit("input", this.data);
            }
        }
    }
};
</script>
<style lang="scss">
@import "../assets/css/themes.scss";
#template-form {
    .multiselect__tags {
        & > * {
            @extend %primary-text;
        }
        @extend %second-background;
    }
}
</style>
<style lang="scss" scoped>
@import "../assets/css/themes.scss";
form {
    position: relative;
    display: grid;
    grid-template-columns: 1fr 2fr 1fr 2fr;
    grid-row-gap: 5px;
    grid-column-gap: 10px;
    align-items: center;
    padding: 10px 30px;

    & > :not(:nth-child(2n)) {
        justify-self: end;
    }

    // negate the loading indicator div
    &.loading > :not(:nth-child(2n + 1)) {
        justify-self: end;
    }

    label[required]::after {
        content: " *";
        font-weight: 800;
        color: red;
    }

    input {
        width: 100%;
        min-height: 40px;
        display: block;
        padding: 8px 40px 0 8px;
        border-radius: 5px;
        border: 1px solid #e8e8e8;
        @extend %primary-border-color;
        @extend %second-background;
        @extend %primary-text;
    }
}
</style>



// WEBPACK FOOTER //
// template-form.vue?3d958ada